/**
 * Export Page Component
 * - Provides comprehensive data export functionality
 * - Supports filtering by date range and export formats
 * - Handles different data types (users, blogs, payments, etc.)
 * - Generates XLSX, CSV, and PDF exports
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiChevronLeft } from "react-icons/fi";
import ExportButton from "../../components/common/ExportButton";
import { useSelector, useDispatch } from "react-redux";
import { fetchBlogs } from "../../redux/slices/blogsCommunitySlice";
import { fetchConsultations } from "../../redux/slices/consultationSlice";
import { fetchPetFinders } from "../../redux/slices/petFinderSlice";
import { fetchPayments } from "../../redux/slices/paymentSlice";
import { fetchPetPlaces } from "../../redux/slices/petPlacesSlice";
import { fetchUsers } from "../../redux/slices/userSlice";

const fieldCls =
  "w-full rounded-md bg-gray-100 px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40";

export default function ExportPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const queryParams = new URLSearchParams(location.search);
  const tab = queryParams.get("tab") || "Appointments";

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [format, setFormat] = useState("CSV");
  

  const blogs = useSelector((state) => state.blogsCommunity.items);
  const consultations = useSelector((state) => state.consultations.items);
  const petFinders = useSelector((state) => state.petFinder.items);
  const payments = useSelector((state) => state.payment.items);
  const petPlaces = useSelector((state) => state.petPlaces.items);
  const usersPetParents = useSelector((state) => state.users.petParents);
  const usersVets = useSelector((state) => state.users.veterinarians);

  useEffect(() => {
    switch (tab) {
      case "BlogsCommunity":
        dispatch(fetchBlogs());
        break;
      case "Consultations":
        dispatch(fetchConsultations());
        break;
      case "PetFinder":
        dispatch(fetchPetFinders());
        break;
      case "Payments":
        dispatch(fetchPayments());
        break;
      case "PetPlaces":
        dispatch(fetchPetPlaces());
        break;
      case "UserManagement":
        dispatch(fetchUsers("pet-parents"));
        dispatch(fetchUsers("veterinarians"));
        break;
      default:
        break;
    }
  }, [dispatch, tab]);

  const currentData = useMemo(() => {
    switch (tab) {
      case "BlogsCommunity":
        return blogs;
      case "Consultations":
        return consultations;
      case "PetFinder":
        return petFinders;
      case "Payments":
        return payments;
      case "PetPlaces":
        return petPlaces;
      case "UserManagement":
        return [...usersPetParents, ...usersVets];
      default:
        return [];
    }
  }, [
    tab,
    blogs,
    consultations,
    petFinders,
    payments,
    petPlaces,
    usersPetParents,
    usersVets,
  ]);

const filteredData = useMemo(() => {
  if (!fromDate && !toDate) return currentData;

  const from = fromDate ? new Date(fromDate) : null;
  const to = toDate ? new Date(toDate) : null;

  if (from) from.setHours(0, 0, 0, 0);
  if (to) to.setHours(23, 59, 59, 999);

  // Define a priority list of date fields
  const dateFields = ["submitted", "date", "datetime", "joined", "lastVisit", "createdAt"];

  return currentData.filter((item) => {
    let itemDate = null;

    for (const field of dateFields) {
      if (item[field]) {
        if (field === "datetime") {
          // remove extra text like "(30mins)"
          const cleanDateTime = item.datetime.split("(")[0].trim();
          itemDate = new Date(cleanDateTime);
        } else {
          itemDate = new Date(item[field]);
        }
        break; // stop after finding first valid date
      }
    }

    if (!itemDate) return false;

    const itemTime = itemDate.getTime();

    if (from && to) return itemTime >= from.getTime() && itemTime <= to.getTime();
    if (from) return itemTime >= from.getTime();
    if (to) return itemTime <= to.getTime();
    return true;
  });
}, [currentData, fromDate, toDate]);



console.log(filteredData);

  const pageTitle = useMemo(() => {
    switch (tab) {
      case "BlogsCommunity":
        return "Export Blogs and Articles Data";
      case "Consultations":
        return "Export Appointments Data";
      case "PetFinder":
        return "Export Pet Finder Listings Data";
      case "Payments":
        return "Export Payments Data";
      case "PetPlaces":
        return "Export Pet Places Data";
      case "UserManagement":
        return "Export Users Data";
      default:
        return "Export Data";
    }
  }, [tab]);

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="mt-1 rounded-md p-1 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
          >
            <FiChevronLeft className="h-5 w-5" />
          </button>

          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{pageTitle}</h1>
            <p className="mt-1 text-sm text-gray-500">
              Select date range and format for your export. Only records within the selected range will appear.
            </p>
          </div>
        </div>

   

      </div>

      {/* Date Range */}
      <div className="mt-8">
        <div className="mb-4 text-sm font-medium text-gray-900">Date Range</div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className={fieldCls}
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              To Date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className={fieldCls}
            />
          </div>
        </div>
      </div>

      {/* Export Format */}
      <div className="mt-8">
        <label className="mb-2 block text-sm font-medium text-gray-700">
          Export Format
        </label>
        <select
          value={format}
          onChange={(e) => setFormat(e.target.value)}
          className={`${fieldCls} max-w-md`}
        >
          <option value="PDF">PDF</option>
          <option value="CSV">CSV</option>
          {/* <option value="XLSX">XLSX</option> */}
        </select>
      </div>

      {/* Records info */}
      <div className="mt-6 w-full rounded-md bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900">
        {filteredData.length} total records available
      </div>

      {/* Footer */}
      <div className="mt-10 flex justify-end gap-3">
        <button
          onClick={() => navigate(-1)}
          className="rounded-md border border-gray-300 bg-white px-8 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50"
        >
          Cancel
        </button>
            <ExportButton
  data={filteredData}
  fileName={`${tab}`}
  format={format}
/>
      </div>
    </div>
  );
}
